/* eslint-disable jsx-a11y/alt-text */
import React, { useState, useRef, forwardRef, useEffect, useImperativeHandle } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Snackbar,
  Alert,
  Typography,
  Button,
} from "@mui/material";

import {
  TwoFactorDto,
  VerifyTwoFactorDto,
  IncreaseFailedAttemptsCountDto,
  PortalAutheticatorAppDto,
} from "../../types/User";
import useApi from "../../hooks/useApi";
import {
  generateQrCode,
  verify2FACode,
  increaseFailedAttemptsCount,
  addSecretKey,
} from "../../services/userService";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import TaskAltSharpIcon from "@mui/icons-material/TaskAltSharp";
import { USERSIGNUPMESSAGES, USERSIGNINMESSAGES } from "../../constants/messages";
import OtpHandler from "../../pages/signup/OtpVerificationComponent";
import { USERINFO, SIGNINRESPONSES, SYSTEMSETTINGS, GLOBALSETTINGS } from "../../data/constants";
import { useSettingValue } from "../../services/useSettingValue";
import OtpInput from "../CommonComponent/CustomOTP";

interface SetUpTwoFactorAuthProps {
  userId: number;
  email: string;
  phone: string;
  userName: string;
  otpSendTo: string;
  onIsSessionLocked: () => void;
}
export interface SetUpTwoFactorAuthRef {
  childComponetCall: () => boolean;
}
const SetUpTwoFactorAuth = forwardRef<SetUpTwoFactorAuthRef, SetUpTwoFactorAuthProps>(
  ({ userId, email, phone, userName, otpSendTo, onIsSessionLocked }, ref) => {
    SetUpTwoFactorAuth.displayName = "";
    const { loading, executeApi } = useApi();
    const [openOTPDialog, setOpenOTPDialog] = useState(false);
    const [tokenErrorMessage, setTokenErrorMessage] = useState("");
    const [showCopiedMessage, setShowCopiedMessage] = useState(false);
    const [isSessionLocked, setIsSessionLocked] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
    const otpHandlerRef = useRef(null);
    const otpRef = useRef(null);
    const [otpInput, setOtpInput] = useState("");

    const [authAppState, setAuthAppState] = useState({
      isSetUp: false,
      isVerified: false,
      isVisible: true,
      qrCode: "",
      secret: "",
    });
    const updateAuthAppState = (newState: Partial<typeof authAppState>) => {
      setAuthAppState((prevState) => ({
        ...prevState,
        ...newState,
      }));
    };
    const otpFieldLength =
      Number(useSettingValue(SYSTEMSETTINGS.OTP_FIELD_LENGTH)) ||
      GLOBALSETTINGS.DEFAULT_OTP_FIELD_LENGTH;
    useImperativeHandle(ref, () => ({
      childComponetCall: () => {
        handleClickOpen();
        return true;
      },
    }));
    const handleGenerate2FA = async () => {
      const emailOrPhone = email?.trim() ? email : phone;
      const data = await executeApi<string, TwoFactorDto>(generateQrCode, emailOrPhone);
      if (data !== null) {
        const qrCodeImg = "data:image/png;base64," + data.qrCodeBase64;
        updateAuthAppState({ qrCode: qrCodeImg, secret: data.secretKey });
        setOpen(true);
        setStep(1);
      }
    };

    const verify2FA = async () => {
      if (!validateToken()) return;
      const verifyTwoFactorData: VerifyTwoFactorDto = {
        secretKey: authAppState.secret,
        code: otpInput,
        email: "",
      };
      const isVerify =
        (await executeApi<VerifyTwoFactorDto, boolean>(verify2FACode, verifyTwoFactorData)) ??
        false;
      updateAuthAppState({
        isVerified: isVerify,
        isSetUp: isVerify ? false : authAppState.isSetUp,
      });

      setTokenErrorMessage(isVerify ? "" : USERSIGNUPMESSAGES.AUTHENTICATION_CODE_DOES_NOT_MATCH);
      if (isVerify) {
        setLoadingVerify(true);
        setTimeout(() => {
          setLoadingVerify(false);
          addSecretKeyData();
          setOpen(false);
        }, 1500);
        updateAuthAppState({ isVisible: false });
      } else {
        onIncreaseFailedAttemptsCount();
      }
    };
    const addSecretKeyData = async () => {
      const authAppData: PortalAutheticatorAppDto = {
        secretKey: authAppState.secret,
        userId: userId,
      };
      const result =
        (await executeApi<PortalAutheticatorAppDto, boolean>(addSecretKey, authAppData)) ?? false;
      if (result) {
        setSnackbarOpen(true);
      }
    };
    const onIncreaseFailedAttemptsCount = async () => {
      const sessionValue = localStorage.getItem(USERINFO.SESSIONVALUE);
      const empAndOtpData: IncreaseFailedAttemptsCountDto = {
        sessionValue: sessionValue,
      };
      const updateResult = await executeApi<IncreaseFailedAttemptsCountDto, string>(
        increaseFailedAttemptsCount,
        empAndOtpData,
      );
      if (updateResult === SIGNINRESPONSES.SESSION_LOCKED) {
        setIsSessionLocked(true);
        onIsSessionLocked();
      }
    };

    const validateToken = () => {
      if (!otpInput) {
        setTokenErrorMessage(USERSIGNUPMESSAGES.AUTHENTICATION_CODE_REQUIRED);
        focusFirstTextField();
        return false;
      }
      if (otpInput.length < otpFieldLength) {
        setTokenErrorMessage(USERSIGNUPMESSAGES.AUTHENTICATION_CODE_6_DIGIT_NUMBER);
        return false;
      }
      setTokenErrorMessage("");
      return true;
    };
    const focusFirstTextField = () => {
      if (!otpRef.current?.isComplete()) {
        otpRef.current?.focusFirstEmpty();
        return;
      }
    };

    const [loadingVerify, setLoadingVerify] = useState(false);
    const [step, setStep] = useState(1);
    const [open, setOpen] = React.useState(false);

    useEffect(() => {
      if (open && step === 2) {
        const timer = setTimeout(() => {
          otpRef.current?.focusFirstEmpty?.();
        }, 150);

        return () => clearTimeout(timer);
      }
    }, [open, step]);
    const handleClickOpen = async () => {
      setTimeout(() => {
        setOpenOTPDialog(true);
        if (otpHandlerRef.current) {
          otpHandlerRef.current.childComponetCall();
          setOpen(false);
        }
      }, 1500);
    };
    const handleClose = () => {
      setOpen(false);
    };
    const handleCopy = () => {
      navigator.clipboard.writeText(authAppState.secret).then(() => {
        setShowCopiedMessage(true);
      });
      setTimeout(() => {
        setShowCopiedMessage(false);
      }, 2000);
    };
    const onCheckOtpVerify = (otp: string, isVerified: boolean) => {
      if (isVerified) {
        handleGenerate2FA();
      }
    };
    const handleCloseDialog = () => {
      setOpenOTPDialog(false);
    };
    const handleIsSessionLocked = () => {
      onIsSessionLocked();
    };
    const handleSnackbarClose = () => setSnackbarOpen(false);

    const handleCustomOtpChange = (otp) => {
      setOtpInput(otp);
    };
    return (
      <>
        {loading && (
          <Box className="circularprogress-box">
            <CircularProgress />
          </Box>
        )}
        <div>
          <OtpHandler
            ref={otpHandlerRef}
            email={email}
            phone={phone}
            userName={userName}
            otpSendTo={otpSendTo}
            onOtpVerified={onCheckOtpVerify}
            onClose={handleCloseDialog}
            open={openOTPDialog}
            onIsSessionLocked={handleIsSessionLocked}
          />
        </div>
        <Dialog
          onClose={handleClose}
          aria-labelledby="customized-dialog-title"
          open={open}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle
            id="customized-dialog-title"
            sx={{ fontWeight: "bold", textAlign: "center" }}
            className="dialogtitle-font-size"
          >
            {step === 3 ? "Verification Successful" : "Set up Multi-Factor Authentication"}
          </DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 12,
              top: 12,
              color: "#fff",
            }}
          >
            <CloseIcon />
          </IconButton>
          <DialogContent dividers sx={{ p: 3, textAlign: "center" }}>
            {step === 1 ? (
              <>
                <Typography variant="body2" color="primary" gutterBottom>
                  STEP 1
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Download an authenticator app such as Microsoft Authenticator or Google
                  Authenticator.
                </Typography>

                <Typography variant="body2" color="primary" sx={{ mt: 2 }} gutterBottom>
                  STEP 2
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Scan the QR Code below with the authenticator app.
                </Typography>
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                  <Paper elevation={1} sx={{ p: "10px" }} variant="outlined">
                    {authAppState.qrCode && (
                      <img src={`${authAppState.qrCode}`} className="sign-in-qrcode" />
                    )}
                  </Paper>
                </Box>

                <Divider sx={{ my: 2, fontSize: "14px" }}>OR enter the code manually</Divider>
                <TextField
                  variant="outlined"
                  fullWidth
                  value={authAppState.secret}
                  sx={{
                    "& .MuiInputBase-input": {
                      fontSize: "14px",
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton edge="end" onClick={handleCopy}>
                          <ContentCopyIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Snackbar
                  open={showCopiedMessage}
                  autoHideDuration={2000}
                  onClose={() => setShowCopiedMessage(false)}
                  message="Copied!"
                  anchorOrigin={{ vertical: "top", horizontal: "right" }}
                >
                  <Alert severity="success">{USERSIGNUPMESSAGES.MANUAL_CODE_COPIED}</Alert>
                </Snackbar>
              </>
            ) : step === 2 ? (
              <>
                <Typography variant="body2" color="primary" gutterBottom>
                  STEP 3
                </Typography>
                {isSessionLocked && (
                  <Alert severity="error" sx={{ marginBottom: "10px" }} className="alert-alert">
                    {USERSIGNINMESSAGES.SESSION_LOCKED_MESSAGE.replace(
                      "{Session_value}",
                      localStorage.getItem(USERINFO.SESSIONVALUE),
                    )}
                  </Alert>
                )}
                <Alert
                  severity="warning"
                  sx={{ marginBottom: "10px" }}
                  className="alert-success-popup"
                >
                  {USERSIGNUPMESSAGES.AUTH_APP_IS_SETUP}
                </Alert>
                <Typography variant="body2" color="textSecondary">
                  {USERSIGNINMESSAGES.TFA_REQUIRED_MESSAGE}
                </Typography>
                <div className={isSessionLocked ? "disabled-div" : ""}>
                  <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                    <Box
                      display="flex"
                      gap={2}
                      sx={{
                        flexWrap: "wrap",
                        width: "100%",
                        maxWidth: {
                          xs: "100%",
                          sm: `${otpFieldLength * 63}px`,
                        },
                      }}
                    >
                      <OtpInput
                        length={otpFieldLength}
                        onChange={handleCustomOtpChange}
                        ref={otpRef}
                      />
                    </Box>
                  </Box>
                </div>
                <p className="required-msg">{tokenErrorMessage}</p>
              </>
            ) : (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <TaskAltSharpIcon sx={{ fontSize: 64, color: "success.light" }} />
                <Typography variant="h1" sx={{ mt: 2 }}>
                  Verified!
                </Typography>
                <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 1 }}>
                  You can now use MFA to verify your identity when logging in.
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
            {step === 2 && (
              <div className={isSessionLocked ? "disabled-div" : ""}>
                <Button
                  variant="contained"
                  color="light"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => setStep(1)}
                  sx={{
                    mr: 1,
                    px: 3,
                    borderRadius: 2,
                  }}
                >
                  Back
                </Button>
              </div>
            )}

            {step === 1 ? (
              <Button
                variant="contained"
                onClick={() => {
                  setOtpInput("");
                  setTokenErrorMessage("");
                  setStep(2);
                }}
                endIcon={<ArrowForwardIcon />}
                sx={{ px: 3, borderRadius: 2 }}
              >
                Continue
              </Button>
            ) : step === 2 ? (
              <>
                <div className={isSessionLocked ? "disabled-div" : ""}>
                  <Button
                    variant="contained"
                    onClick={verify2FA}
                    endIcon={
                      loadingVerify ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <CheckCircleOutlineIcon />
                      )
                    }
                    sx={{ px: 3, borderRadius: 2 }}
                    disabled={loadingVerify}
                  >
                    {loadingVerify ? "Verifying..." : "Verify"}
                  </Button>
                </div>
              </>
            ) : (
              <Button variant="contained" onClick={handleClose} sx={{ px: 3, borderRadius: 2 }}>
                Done
              </Button>
            )}
          </DialogActions>
        </Dialog>
        <Snackbar
          open={snackbarOpen}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          autoHideDuration={15000}
          onClose={handleSnackbarClose}
        >
          <Alert onClose={handleSnackbarClose} severity="success">
            {USERSIGNINMESSAGES.TFA_SETUP_DONE}
          </Alert>
        </Snackbar>
      </>
    );
  },
);
export default SetUpTwoFactorAuth;
